import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Laptop, Monitor, Server, Network, Printer, BatteryCharging, MonitorSmartphone, Armchair, Tv, Cpu, Package } from "lucide-react";
import Header from "./Header";
import Footer from "./Footer";
import FloatingWhatsApp from "./FloatingWhatsApp";
import useSeo from "../hooks/useSeo";
import { InquiryForm } from "./InquiryForm";

// Label + icon for known categories; tiles are driven by LIVE inventory so navigation
// never points at an empty category and every stocked category is reachable.
const CAT_META = {
  "Laptop": { label: "Laptops", Icon: Laptop },
  "Desktop": { label: "Desktops", Icon: Monitor },
  "Accessories & IT Spares": { label: "Accessories & IT Spares", Icon: Cpu },
  "Office Equipment": { label: "Office Equipment", Icon: Armchair },
  "Server": { label: "Servers", Icon: Server },
  "Networking": { label: "Networking", Icon: Network },
  "Printer": { label: "Printers", Icon: Printer },
  "UPS": { label: "UPS", Icon: BatteryCharging },
  "Monitor": { label: "Monitors", Icon: MonitorSmartphone },
  "Furniture": { label: "Office Furniture", Icon: Armchair },
  "TV": { label: "AC / TV", Icon: Tv },
};
const metaFor = (cat) => CAT_META[cat] || { label: cat, Icon: Package };

export default function StorePage() {
  useSeo({
    title: "Store \u2014 Buy Pre-Owned IT & Office Equipment | Kapatia",
    description: "Browse the Kapatia store by category \u2014 laptops, desktops, servers, networking, printers, UPS, monitors, office furniture and AC/TV. Working-condition pre-owned business assets with quick WhatsApp enquiry.",
  });

  const [counts, setCounts] = useState(null);
  useEffect(() => {
    let alive = true;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/products`)
      .then((r) => r.json())
      .then((list) => {
        if (!alive || !Array.isArray(list)) return;
        const c = {};
        list.forEach((p) => { if (p && p.category) c[p.category] = (c[p.category] || 0) + 1; });
        setCounts(c);
      })
      .catch(() => alive && setCounts({}));
    return () => { alive = false; };
  }, []);

  const [showChips, setShowChips] = useState(false);
  useEffect(() => {
    const onScroll = () => setShowChips(window.scrollY > 180);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const countLabel = (cat) => {
    if (counts === null) return "\u2026";
    const n = counts[cat] || 0;
    return n > 0 ? `${n} item${n === 1 ? "" : "s"}` : "Ask availability";
  };

  // Only show categories that actually have stock, most-stocked first.
  const cats = counts
    ? Object.keys(counts).filter((c) => counts[c] > 0).sort((a, b) => counts[b] - counts[a])
    : [];

  return (
    <div className="App public-site">
      <Header />

      {/* Scroll-activated category chip bar — mobile only, keeps categories one tap away */}
      <nav aria-label="Shop by category" data-testid="store-chip-bar"
           className={`fixed inset-x-0 top-0 z-40 border-b sm:hidden transition-transform duration-300 ${showChips ? "translate-y-0" : "-translate-y-full"}`}
           style={{ background: "rgba(255,253,247,0.96)", backdropFilter: "blur(10px)", WebkitBackdropFilter: "blur(10px)", borderColor: "var(--line)" }}>
        <div className="no-scrollbar flex gap-2 overflow-x-auto px-4 py-2.5">
          {cats.map((cat) => {
            const { label } = metaFor(cat);
            return (
            <Link key={cat} to={`/stock/${encodeURIComponent(cat)}`} data-testid={`store-chip-${cat}`}
                  className="flex shrink-0 items-center gap-1.5 rounded-full border px-3.5 py-2 font-heading text-[13px] font-bold"
                  style={{ borderColor: "var(--line)", background: "#fff", color: "var(--green-900)" }}>
              {label}
              {counts && counts[cat] > 0 && (
                <span className="rounded-full px-1.5 text-[11px] font-bold" style={{ background: "var(--green-100)", color: "var(--green-800)" }}>{counts[cat]}</span>
              )}
            </Link>
            );
          })}
        </div>
      </nav>

      <section className="relative overflow-hidden grid-backdrop"
        style={{ background: "linear-gradient(135deg, rgba(255,248,236,0.98), rgba(255,253,247,0.9)), radial-gradient(circle at 84% 18%, rgba(245,180,0,0.22), transparent 28%)" }}>
        <div className="container py-12">
          <p className="eyebrow">Kapatia Store</p>
          <h1 data-testid="store-heading" className="mt-2 font-heading font-semibold leading-[1.05]" style={{ color: "var(--green-950)", fontSize: "clamp(30px,5vw,52px)" }}>
            Buy Pre-Owned <span style={{ color: "var(--orange-700)" }}>IT & Office Equipment</span>
          </h1>
          <p className="mt-3 max-w-[640px] text-[16px] font-medium" style={{ color: "#31463a" }}>
            Choose a category to see live inventory. All listed items are in good working condition, priced for business buyers.
          </p>
          <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4" data-testid="store-categories">
            {counts === null ? (
              Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="min-h-[172px] animate-pulse rounded-2xl border bg-white p-5" style={{ borderColor: "var(--line)" }} />
              ))
            ) : cats.map((cat) => {
              const { label, Icon } = metaFor(cat);
              return (
              <Link key={cat} to={`/stock/${encodeURIComponent(cat)}`} data-testid={`store-cat-${cat}`}
                    className="group flex min-h-[172px] flex-col items-start justify-between gap-3 rounded-2xl border bg-white p-5 transition-transform active:scale-[0.98] hover:-translate-y-1"
                    style={{ borderColor: "var(--line)", boxShadow: "0 12px 30px rgba(45,106,79,0.08)" }}>
                <span className="grid h-14 w-14 place-items-center rounded-xl" style={{ background: "var(--green-100)", color: "var(--green-800)" }}><Icon size={28} /></span>
                <div className="w-full">
                  <span className="block font-heading text-[17px] font-bold leading-tight" style={{ color: "var(--green-950)" }}>{label}</span>
                  <span className="mt-1 block text-[13px] font-semibold" style={{ color: "#5b6b60" }} data-testid={`store-cat-count-${cat}`}>{countLabel(cat)}</span>
                </div>
                <span className="text-[14px] font-bold" style={{ color: "var(--orange-700)" }}>View inventory &rarr;</span>
              </Link>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-12" style={{ background: "var(--paper)" }}>
        <div className="container max-w-xl">
          <InquiryForm campaign="store" heading="Can't find it? Tell us what you need"
                       requirementPlaceholder="e.g. 20 laptops, 5 UPS systems, or a specific model" />
        </div>
      </section>

      <Footer />
      <FloatingWhatsApp />
    </div>
  );
}
